import { Helmet } from "react-helmet-async";
import { bureauServices } from "../../assets/bureau/bureauServices";
import structuredData from "../../assets/structuredText";
import BureauHero from "../../components/BureauHero";
import IconCard from "../../components/IconCard";
import SectionHeading from "../../components/SectionHeading";
import Subheading from "../../components/Subheading";
import usePageReset from "../../hooks/usePageReset";
import { motion, type Variants } from "framer-motion";
import { isMobile } from "react-device-detect";

const Bureau = () => {
  usePageReset();

  const moveUp: Variants = {
    hidden: { opacity: 0, y: 60 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeInOut",
        delay: i * 0.15,
      },
    }),
  };

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta
          name="keywords"
          content="outsourcing soft-landing business growth out-staffing tourism recruitment market-penetration"
        />
        <meta
          name="description"
          content="Cultiv Bureau: Expert outsourcing, out-staffing, and recruitment services tailored to boost your business efficiency and growth."
        />
        <meta name="robots" content="index, follow" />
        <meta property="og:url" content="https://cultiv.llc" />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          itemProp="image"
          content="https://cultiv.llc/images/metaimg.jpg"
        />
        <link rel="image_src" itemProp="image" href="https://cultiv.llc/images/metaimg.jpg" />
        <meta
          property="og:description"
          content="Cultiv Bureau: Expert outsourcing, out-staffing, and recruitment services tailored to boost your business efficiency and growth."
        />
        <meta property="og:site_name" content="Cultiv LLC" />
        <meta property="og:title" content="Cultiv | Bureau" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://cultiv.llc" />
        <meta property="twitter:title" content="Cultiv | Bureau" />
        <meta
          property="twitter:description"
          content="Cultiv Bureau: Expert outsourcing, out-staffing, and recruitment services tailored to boost your business efficiency and growth."
        />
        <meta property="twitter:image" content="https://cultiv.llc/images/metaimg.jpg" />
        <link rel="canonical" href="https://cultiv.llc" />
        <script type="application/ld+json">{structuredData}</script>

        <title>Cultiv Bureau</title>
      </Helmet>

      <div className="flex flex-col gap-6 lg:gap-10 gap-y-0 lg:gap-y-0 w-full">
        <BureauHero />

        {/* About Us section */}

        <section className="px-6 xl:px-30 py-12 xl:py-30  grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-12 gap-4 md:gap-6 xl:gap-8 xl:gap-y-0">
          <div className="col-span-full xl:col-span-6">
            <SectionHeading text="Get to know us" textColor="text-cultiv-yellow" />
            <p className="text-sm md:text-base 3xl:text-lg flex items-center pb-12 pt-3 md:pt-6">
              This program is designed to help determine the ideal soft-landing strategy for your
              business in Egypt. This program is tailored to your unique needs and provides valuable
              resources to navigate the Egyptian market effectively.
            </p>
            <div className="flex flex-col gap-4">
              <Subheading text="About Us & Our Values" />
              <p className="text-sm md:text-base 3xl:text-lg flex items-center">
                Cultiv Bureau is part of the Cultiv Collective, a dynamic ecosystem of
                interconnected companies fostering innovation across entrepreneurship and beyond.
                Established in 2019, Established in 2019, Cultiv has been instrumental in numerous
                impactful projects, solidifying its position as a veteran company in the innovation
                landscape. With years of experience, Cultiv continues to lead and collaborate across
                industries, shaping the future of creative enterprise
              </p>
              <div className="xl:hidden flex col-span-full xl:col-span-6  gap-4 xl:gap-8 items-center">
                <img
                  src="/images/bureau/bureauLogoWireframe.svg"
                  alt="Working steps to finished product"
                />
                <div className="flex flex-col gap-4 xl:gap-8">
                  <div className="flex items-center gap-2 xl:gap-4">
                    <img
                      src="/images/bureau/bureauMan.webp"
                      alt="Diversity & Equity inclusion"
                      className="w-full"
                    />
                  </div>
                  <p className="text-sm md:text-base 3xl:text-lg flex items-center">
                    We enhance your Market Research and strategy development through dedicated
                    office hours and tailored workshops, ensuring you’re well-equipped to make
                    informed decisions.
                  </p>
                </div>
              </div>
              <p className="text-sm md:text-base 3xl:text-lg flex items-center">
                We empower businesses with comprehensive support that drives success. Our expert
                mentorship program offers 300 minutes of Guidance from seasoned industry leaders,
                helping you navigate challenges and refine your strategies.
              </p>
              <p className="text-sm md:text-base 3xl:text-lg flex items-center">
                We Simplify Entry process providing discounted access to our infrastructure,
                significantly reducing operational costs and easing your soft-landing experience. We
                give access to Egypt's highly skilled, multilingual Talent Pool through flexible
                outsourcing and dedicated project teams.
              </p>
            </div>
          </div>
          <div className="hidden xl:flex col-span-full xl:col-span-6  gap-4 xl:gap-8 items-center">
            <img
              src="/images/bureau/bureauLogoWireframe.svg"
              alt="Working steps to finished product"
            />
            <div className="flex flex-col gap-4 xl:gap-8">
              <div className="flex items-center gap-2 xl:gap-4">
                <img
                  src="/images/bureau/bureauMan.webp"
                  alt="Diversity & Equity inclusion"
                  className="w-full"
                />
              </div>
              <p className="text-sm md:text-base 3xl:text-lg flex items-center">
                We enhance your Market Research and strategy development through dedicated office
                hours and tailored workshops, ensuring you’re well-equipped to make informed
                decisions.
              </p>
            </div>
          </div>
        </section>

        <section className="px-6 xl:px-30 pb-12 xl:pb-30  grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-12 gap-4 md:gap-6 xl:gap-8 xl:gap-y-4">
          <SectionHeading text="Services We Offer" className="col-span-full text-cultiv-yellow" />
          <div className="flex flex-col gap-4 col-span-full xl:col-span-9 xl:pb-11">
            <p className="text-sm md:text-base 3xl:text-lg flex items-center">
              Cultiv Bureau offers a wide range of services tailored to meet the diverse needs of
              startups and SMEs. Our core offerings include outsourcing, out-staffing, recruitment,
              technology resale, and consulting services. By leveraging our extensive network and
              expertise, we ensure that your business operations run smoothly and efficiently,
              allowing you to focus on growth and innovation.
            </p>
          </div>
          <motion.div
            className="grid grid-cols-subgrid col-span-full w-full gap-y-8 lg:gap-y-0"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: isMobile ? 0.15 : 0.65 }}
          >
            {bureauServices.map((service, i) => (
              <motion.div
                key={service.name}
                custom={i}
                variants={moveUp}
                className="col-span-full md:col-span-3"
              >
                <IconCard
                  src={service.icon}
                  className="col-span-full xl:col-span-3"
                  imageClassName="w-[152px]"
                  key={service.name}
                  route="/"
                  hoverSrc="/"
                  alt="/"
                  title={service.name}
                  description={service.description}
                />
              </motion.div>
            ))}
          </motion.div>
        </section>
        <section className="bg-cultiv-yellow px-6 py-12 md:p-15 lg:p-20 xl:p-30 grid grid-cols-4 lg:grid-cols-12 gap-6 lg:gap-8 relative">
          <h4 className="font-expandedMedium capitalize text-xl md:text-2xl lg:text-3xl xl:text-5xl xl:leading-[60px] flex flex-col col-span-4 lg:col-span-7 xl:col-start-2">
            Ready to cultivate your success in Egypt?
          </h4>
          <div
            className={`grid items-center row-start-2 xl:col-start-2 grid-cols-subgrid col-span-full`}
          >
            <p className="text-sm md:text-base 3xl:text-lg flex items-center col-span-full xl:col-span-7 pb-4 xl:pb-0">
              Contact Cultiv Bureau today and discover how our tailored soft-landing strategies can
              help you thrive in this dynamic market.
            </p>
            <a
              href="https://bureau.cultiv.llc/contact"
              className={`flex items-center w-full col-span-3 justify-center font-extendedMedium  hover:text-white bg-transparent hover:bg-black border-2 border-black transition-all duration-300 ease-in-out px-7 lg:px-14 h-8 md:h-10 lg:h-12 `}
            >
              <p className="text-sm md:text-lg lg:text-base xl:text-xl font-extendedMedium whitespace-nowrap">
                Get in touch
              </p>
            </a>
          </div>
        </section>
      </div>
    </>
  );
};

export default Bureau;
